/**
 * Pending-metadata persistence for freshly-launched grids.
 *
 * After `createGrid` confirms, the wizard tries to POST the creator's
 * picks (name, description, plinth, skybox, socials) to
 * `/api/grid/[ugm]/[gridId]/metadata`. That POST can fail mid-flight for
 * reasons that have nothing to do with the user's intent:
 *
 *  - The Privy access token expired between the seat-init confirm and
 *    the metadata POST.
 *  - Envio temporarily 503s and the on-chain RPC fallback in
 *    `requireGridCreator` also blips.
 *  - The user's network drops while the wizard navigates them to the
 *    board page, killing the in-flight fetch.
 *
 * When that happens the board ends up live on-chain but with empty
 * `pool_metadata`, which the lobby renders as "Grid #N" + the
 * adapter-derived skybox/plinth defaults — exactly the bug surface the
 * user reported (marble/desert reverting to wood/homestead, generic
 * names, no image).
 *
 * Strategy: stash the wizard's payload in `localStorage` keyed by the
 * composite gridId BEFORE the POST. On success we clear the entry; on
 * failure we leave it. `LobbyClient` checks for a pending entry on
 * board mount and re-runs the POST with the same payload (and a fresh
 * access token), achieving "guaranteed delivery" semantics across:
 *
 *  - Network blips during the launch flow.
 *  - Page navigation / app-shell remounts.
 *  - Browser refreshes mid-launch.
 *  - Wallet disconnects + reconnects (the retry uses whatever access
 *    token is current at retry time, not the one captured at launch).
 *
 * The payload mirrors the body the metadata POST accepts; we include
 * the `managerAddress` so the retry path doesn't have to re-derive the
 * URL from the gridId alone.
 */

const STORAGE_KEY = 'takeover:pending-grid-metadata:v1';

export interface PendingMetadataPayload {
  /** Composite gridId — `${ugm.toLowerCase()}-${numericId}`. Used as
   *  the localStorage map key AND as the `compositeGridId` the lobby
   *  uses to identify boards. */
  compositeGridId: string;
  /** UnifiedGridManager address the grid lives on. Lower-cased. */
  managerAddress: string;
  /** Numeric gridId as a decimal string (bigints aren't JSON-safe). */
  numericId: string;
  /** Exact body to POST to `/api/grid/[ugm]/[gridId]/metadata`. */
  body: Record<string, unknown>;
  /** When the entry was first written (ms since epoch). Used by the
   *  retry path to expire stale drafts after a few hours so we don't
   *  resurrect ancient picks for a board the user has since edited
   *  manually via the Info panel. */
  createdAt: number;
}

type PendingMap = Record<string, PendingMetadataPayload>;

/** Maximum age of a pending entry before the lobby gives up retrying.
 *  Generous because the user might create a board, walk away, and come
 *  back hours later — but bounded so a forgotten entry doesn't fight
 *  with a manual edit indefinitely. */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function readMap(): PendingMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as PendingMap;
  } catch {
    return {};
  }
}

function writeMap(map: PendingMap): void {
  if (typeof window === 'undefined') return;
  try {
    if (Object.keys(map).length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage can throw on quota or in incognito profiles. The
    // worst-case fallback is "no retry persistence", which leaves the
    // launch flow back at its pre-fix behaviour — not a regression.
  }
}

/** Stash a pending metadata payload. Overwrites any existing entry for
 *  the same compositeGridId — the latest write always wins, which
 *  matches user intent when they re-launch the same board id (rare,
 *  but possible across multi-UGM environments). */
export function setPendingMetadata(payload: PendingMetadataPayload): void {
  const map = readMap();
  map[payload.compositeGridId] = payload;
  writeMap(map);
}

/** Read the pending entry for a compositeGridId, or `null` if there's
 *  nothing pending or the entry has aged out. Aged-out entries are
 *  garbage-collected on read so the map doesn't grow unbounded. */
export function getPendingMetadata(
  compositeGridId: string,
): PendingMetadataPayload | null {
  const map = readMap();
  const entry = map[compositeGridId];
  if (!entry) return null;
  if (Date.now() - entry.createdAt > MAX_AGE_MS) {
    delete map[compositeGridId];
    writeMap(map);
    return null;
  }
  return entry;
}

/** Drop the pending entry for a compositeGridId — call on a successful
 *  POST so we don't keep retrying writes that already landed. */
export function clearPendingMetadata(compositeGridId: string): void {
  const map = readMap();
  if (!(compositeGridId in map)) return;
  delete map[compositeGridId];
  writeMap(map);
}

/** Return the list of compositeGridIds with pending writes. Used by
 *  background sweep paths (e.g. a one-shot effect on lobby mount that
 *  retries every pending board, not just the active one). */
export function listPendingMetadata(): PendingMetadataPayload[] {
  const map = readMap();
  const now = Date.now();
  const fresh: PendingMetadataPayload[] = [];
  let mutated = false;
  for (const [key, entry] of Object.entries(map)) {
    if (now - entry.createdAt > MAX_AGE_MS) {
      delete map[key];
      mutated = true;
      continue;
    }
    fresh.push(entry);
  }
  if (mutated) writeMap(map);
  return fresh;
}
